/**
 * VpnBlockedScreen
 *
 * Pantalla de bloqueo full-screen para visitantes con VPN/proxy/Tor detectado
 * por iplocate (privacy.isAnonymous). Se muestra en lugar de la landing
 * exclusiva (USExclusiveLanding / DualCTAVideoLanding) cuando el snapshot del
 * worker marca `isAnonymous`. El contenido premium no se renderiza detrás —
 * el visitante no "entra" con VPN.
 *
 * Bilingüe (es/en). Botón "reintentar" recarga (por si desactivan el VPN).
 * Estética alineada a TemplateLoadingScreen (dark canvas + brand wordmark).
 */

import { BRAND_NAME, BRAND_PERSON_NAME } from "@/lib/brandConfig";
import { ShieldAlert } from "lucide-react";
import type { FC } from "react";

interface VpnBlockedScreenProps {
  /** Idioma de la copy. Default "en" (público US + resto del mundo). */
  lang?: "es" | "en";
  "data-testid"?: string;
}

const COPY = {
  es: {
    title: "Contenido no disponible con VPN",
    body: "Detectamos una VPN, proxy o red anónima. Para proteger el contenido exclusivo, no se puede acceder con estas conexiones.",
    action: "Desactivá tu VPN y volvé a intentar",
    retry: "Reintentar",
  },
  en: {
    title: "Not available over VPN",
    body: "We detected a VPN, proxy, or anonymizing network. To protect exclusive content, access isn't allowed over these connections.",
    action: "Turn off your VPN and try again",
    retry: "Retry",
  },
} as const;

export const VpnBlockedScreen: FC<VpnBlockedScreenProps> = ({
  lang = "en",
  "data-testid": dataTestId,
}) => {
  const brandName = (BRAND_PERSON_NAME || BRAND_NAME || "").trim();
  const c = COPY[lang];

  return (
    <div
      className="fixed inset-0 z-[9998] flex flex-col items-center justify-center bg-[#0a0a0a] text-center"
      role="alert"
      aria-live="assertive"
      data-testid={dataTestId}
      style={{
        backgroundImage:
          "radial-gradient(ellipse at center, rgba(255,255,255,0.04) 0%, rgba(0,0,0,0) 60%)",
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
        paddingLeft: "env(safe-area-inset-left)",
        paddingRight: "env(safe-area-inset-right)",
        minHeight: "100svh",
      }}
    >
      <div className="flex max-w-md flex-col items-center gap-5 px-8">
        {brandName && (
          <span className="text-white/70 text-[13px] font-display font-semibold tracking-[0.2em] uppercase select-none">
            {brandName}
          </span>
        )}

        <span
          className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-500/10 ring-1 ring-amber-500/25"
          aria-hidden
        >
          <ShieldAlert className="h-8 w-8 text-amber-400" />
        </span>

        <h1 className="text-white text-[22px] sm:text-[24px] font-semibold leading-tight tracking-tight">
          {c.title}
        </h1>

        <p className="text-white/60 text-[14px] leading-relaxed">{c.body}</p>

        <p className="text-amber-300/90 text-[13px] font-medium">{c.action}</p>

        <button
          type="button"
          onClick={() => {
            if (typeof window !== "undefined") window.location.reload();
          }}
          className="mt-2 rounded-full border border-white/15 bg-white/[0.06] px-6 py-2.5 text-[14px] font-medium text-white transition-colors hover:bg-white/[0.12]"
        >
          {c.retry}
        </button>
      </div>
    </div>
  );
};
